// @ts-nocheck
// Agent Portal (/cti2) — the agent's whole break flow in one self-contained
// rail panel: pick a break → start it → watch the countdown → end it.
// Replaces the "Today's Stats" panel that used to sit in this slot (its
// Set/Remove Break button was the only way in, which is why break controls
// lived inside a stats panel at all).
//
// Deliberately NOT a modal. The previous flow opened a shadcn Dialog from
// inside AgentPortalPage's `.ap-shell` portal (z-[1000]) without the
// `z-[1100]` override that TransferConferencePicker.tsx documents — so the
// break picker rendered *behind* the shell and clicking "Set Break"
// appeared to do nothing at all. Inline in the rail there's no portal, no
// stacking context to lose, and one less thing between the agent and a
// break.
//
// Reads BreakContext directly rather than taking ten props: everything here
// (catalogue, selection, start/stop, countdown, exceeded) is that context's
// state, and it's provided app-wide by BreakProvider. useCallStore is only
// consulted for the "can't go on break mid-call" guard, which is the same
// condition the old panel used.
import { useContext, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Ban } from 'lucide-react';
import { Coffee, LogOut, Play, RefreshCw } from 'lucide-react';
import { BreakContext } from '@/router/context/BreakContext';
import { useCallStore } from '@/router/zustand/callStore';
import { getUser } from '@/utilities/setting';
import { formatSeconds } from '@/features/cti/ctiHelpers';
import { useModuleOps } from '../hooks/useModuleOps';
import { ACL_DENIED_TEXT, breakAccess } from '../acl';

function BreakChoice({ brk, selected, onSelect, disabled }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => onSelect(brk.id)}
      className={`flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
        selected ? 'bg-primary/15' : 'bg-muted/30 hover:bg-muted/60'
      }`}
    >
      <span
        className={`h-1.5 w-1.5 shrink-0 rounded-full ${selected ? 'bg-primary' : 'bg-muted-foreground/40'}`}
      />
      <span className="min-w-0 flex-1 truncate text-[11px] font-medium text-foreground">
        {brk.title}
      </span>
      {brk.duration != null && (
        <span className="shrink-0 text-[10px] tabular-nums text-muted-foreground">
          {brk.duration}m
        </span>
      )}
    </button>
  );
}

export default function AgentBreakPanel() {
  const {
    userStatus,
    breaksData = [],
    selectedBreakId,
    setSelectedBreakId,
    startBreak,
    stopBreak,
    fetchBreaks,
    activeBreak,
    countdown,
    breakExceeded,
    breakEndTime,
  } = (useContext(BreakContext) as any) || {};

  const callState = useCallStore((s) => s.callState);
  const dispositionActive = useCallStore((s) => s.dispositionActive);
  const dispositionOpen = useCallStore((s) => s.dispositionOpen);
  const moduleOps = useModuleOps();
  const { canUseBreaks } = breakAccess(moduleOps);
  const [busy, setBusy] = useState(false);
  const [loadingBreaks, setLoadingBreaks] = useState(false);
  const [overBy, setOverBy] = useState(0);

  const onBreak = userStatus === 'BREAK';
  // Same guard the previous panel used — a break can't start mid-call.
  const busyOnCall = ['oncall', 'connected', 'hold', 'conference', 'dialing', 'incoming'].includes(
    callState,
  );
  // A call ending doesn't mean the agent is free — wrap-up (disposition)
  // still has to be finished first. This was never checked here, so an
  // agent could start a break in the middle of wrap-up and leave the
  // disposition unsubmitted.
  const busyOnDisposition = Boolean(dispositionActive || dispositionOpen);

  // BreakContext's own countdown interval clears itself the moment a break
  // is exceeded, so "over by" has to tick off breakEndTime independently.
  useEffect(() => {
    if (!onBreak || !breakExceeded || !breakEndTime) return undefined;
    const tick = () => setOverBy(Math.max(0, Math.floor((Date.now() - breakEndTime) / 1000)));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [onBreak, breakExceeded, breakEndTime]);

  const breaks = useMemo(() => (Array.isArray(breaksData) ? breaksData : []), [breaksData]);

  useEffect(() => {
    if (!canUseBreaks || onBreak || breaks.length || !fetchBreaks) return;
    setLoadingBreaks(true);
    Promise.resolve(fetchBreaks())
      .catch(() => {})
      .finally(() => setLoadingBreaks(false));
  }, [canUseBreaks, onBreak, breaks.length, fetchBreaks]);

  const reloadBreaks = () => {
    if (loadingBreaks || !fetchBreaks) return;
    setLoadingBreaks(true);
    Promise.resolve(fetchBreaks())
      .catch(() => {})
      .finally(() => setLoadingBreaks(false));
  };

  const run = async (action) => {
    setBusy(true);
    try {
      await action();
    } finally {
      setBusy(false);
    }
  };

  const handleStart = () => {
    if (busyOnDisposition) {
      toast.error('Finish your disposition/wrap-up before taking a break.');
      return;
    }
    return run(() => startBreak?.(selectedBreakId));
  };
  // stopBreak takes the user id (it builds USER_BREAKS_EDIT from it) — same
  // value AgentPortalPage's own End Break handler passes.
  const handleStop = () => run(() => stopBreak?.(getUser()?.id));

  return (
    <div className="cri-panel cri-stats-panel">
      <div className="cri-panel__head">
        <Coffee size={13} className="cri-panel__ico" />
        <span className="cri-panel__title">{onBreak ? 'On break' : 'Break'}</span>
        {onBreak && activeBreak?.title ? (
          <span className="cri-panel__count">{activeBreak.title}</span>
        ) : null}
        {!onBreak && canUseBreaks ? (
          <button
            type="button"
            onClick={reloadBreaks}
            disabled={loadingBreaks}
            title="Reload breaks"
            aria-label="Reload breaks"
            className="ml-auto inline-flex h-5 w-5 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-50"
          >
            <RefreshCw size={11} className={loadingBreaks ? 'animate-spin' : ''} />
          </button>
        ) : null}
      </div>

      {onBreak ? (
        <div className="flex flex-col items-center gap-1.5 px-3 py-4 text-center">
          <div
            className={`font-mono text-2xl font-bold tabular-nums ${breakExceeded ? 'text-destructive' : 'text-foreground'}`}
          >
            {formatSeconds(breakExceeded ? overBy : Math.max(0, Number(countdown) || 0))}
          </div>
          <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
            {breakExceeded ? 'Over time' : 'Time remaining'}
          </div>
          <button
            type="button"
            className="cri-stats-break cri-stats-break--on mt-2 inline-flex w-full items-center justify-center gap-1.5"
            onClick={handleStop}
            disabled={busy}
          >
            <LogOut size={12} />
            {busy ? 'Ending…' : 'End break'}
          </button>
        </div>
      ) : !canUseBreaks ? (
        <div className="flex flex-col items-center gap-1.5 px-3 py-5 text-center">
          <Ban size={16} className="text-muted-foreground" />
          <div className="cri-empty">{ACL_DENIED_TEXT.breaks}</div>
        </div>
      ) : (
        <div className="flex flex-col gap-2 px-2.5 py-2.5">
          {breaks.length === 0 ? (
            <div className="flex flex-col items-center gap-1.5 py-2">
              <div className="cri-empty">{loadingBreaks ? 'Loading breaks…' : 'Breaks unavailable'}</div>
              {!loadingBreaks && (
                <button
                  type="button"
                  onClick={reloadBreaks}
                  className="text-[11px] font-semibold text-primary hover:underline"
                >
                  Retry
                </button>
              )}
            </div>
          ) : (
            <div className="ap-noscrollbar max-h-40 space-y-1 overflow-y-auto">
              {breaks.map((brk) => (
                <BreakChoice
                  key={brk.id}
                  brk={brk}
                  selected={String(selectedBreakId ?? '') === String(brk.id)}
                  onSelect={setSelectedBreakId}
                  disabled={busy || busyOnCall || busyOnDisposition}
                />
              ))}
            </div>
          )}
          <button
            type="button"
            className="cri-stats-break inline-flex items-center justify-center gap-1.5"
            onClick={handleStart}
            disabled={busy || busyOnCall || busyOnDisposition || !selectedBreakId}
          >
            <Play size={11} />
            {busy ? 'Starting…' : 'Start break'}
          </button>
          {busyOnCall ? (
            <div className="text-center text-[10px] text-muted-foreground">
              Finish the active call to take a break
            </div>
          ) : busyOnDisposition ? (
            <div className="text-center text-[10px] text-muted-foreground">
              Finish your disposition to take a break
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}
